import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { UncontrolledTooltip } from "reactstrap";
import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { AttachmentFile } from "src/model/Attachment";
import { FileUploadRequest, FileUploadRequestFromAttachmentFile } from "src/requests/CommonRequest";
import { CreditApplicationRequest, CreditApplicationStateRequest } from "src/requests/CustomerRequests";
import { CustomerStore } from "src/stores/CustomerStore";

import { faDollarSign, faDownload, faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as fileUtil from "../../common/Methods/FileUtil";
import {
    CreditApplication,
    CreditApplicationState,
    CreditApplicationStateFriendlyName,
    OnTheSpotCreditApplication,
    PermanentCreditApplication
} from "../../model/Customer";
import AttachmentsForm from "../common/AttachmentsForm";
import DropDownButton from "../common/DropdownButton";
import { HorseForSaleLink } from "../common/HorseForSaleLink";
import LoadingWidget from "../common/LoadingWidget";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { InputGroup, TextareaGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";

namespace EditablePermanentCreditApplication {
    export interface Fields {
        amountRequested: string;
        notes: string;
        attachmentLog: Array<FileUploadRequest>;
    }

    export const Empty = (): Fields => {
        return {
            amountRequested: "",
            notes: "",
            attachmentLog: []
        };
    };

    export const FromModel = (m: PermanentCreditApplication): Fields => {
        return {
            amountRequested: (m.amountRequested / 100).toFixed(2),
            notes: m.notes || "",
            attachmentLog: m.attachmentLog.files.map(f => FileUploadRequestFromAttachmentFile(f))
        };
    };

    export const ToRequest = (f: Fields): CreditApplicationRequest => {
        return {
            amountRequested: Math.floor(parseFloat(f.amountRequested.replace(/,/g, "")) * 100),
            notes: f.notes,
            attachments: f.attachmentLog
        };
    };
}

namespace EditableCreditApplicationStateChange {
    export interface Fields {
        state: CreditApplicationState | null;
    }

    export const Empty = (): Fields => {
        return { state: null };
    };
    export const ToRequest = (f: Fields): CreditApplicationStateRequest => {
        if (f.state === null) {
            throw new Error("In EditableCreditApplicationStateChange::ToRequest with a null state?");
        }
        return { state: f.state };
    };
}

export const CreditApplicationStateToPresent = (state: CreditApplicationState): string => {
    switch (state) {
        case CreditApplicationState.APPROVED:
            return "Approve";
        case CreditApplicationState.REVOKED:
            return "Revoke";
        case CreditApplicationState.REJECTED:
            return "Reject";
        default:
            return "?";
    }
};

export interface Props {
    customerStore?: CustomerStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
}

export interface ComponentState {
    wasValidated: boolean;
    showForm: boolean;
    editableApplicationId: number | null;
}

type State = ComponentState & EditablePermanentCreditApplication.Fields & EditableCreditApplicationStateChange.Fields;

const DefaultState = (): State => {
    return {
        ...EditablePermanentCreditApplication.Empty(),
        ...EditableCreditApplicationStateChange.Empty(),
        wasValidated: false,
        showForm: false,
        editableApplicationId: null
    };
};

@inject("attachmentLogEndpoints")
@inject("customerStore")
@observer
export default class CreditApplicationComponent extends React.Component<Props, State> {
    state: State = DefaultState();

    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
    }

    creditApplicationStateChange = (id: number, state: CreditApplicationState) => async (event: React.MouseEvent<HTMLButtonElement>) => {
        try {
            const addApprovedWord = (state as CreditApplicationState) === "REVOKED" ? "approved" : "";
            const goAhead = await SimpleConfirmation(
                `Are you sure you'd like to 
                ${CreditApplicationStateToPresent(state as CreditApplicationState)} this ${addApprovedWord} credit application?`
            );
            if (!goAhead) {
                return;
            }
            await this.props.customerStore!.creditApplicationStateChange(id, state);
            successMessage(`This credit application status has been changed.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    generateRequest = (): CreditApplicationRequest => {
        return EditablePermanentCreditApplication.ToRequest(this.state);
    };

    createCreditApplication = async () => {
        try {
            const request = this.generateRequest();
            await this.props.customerStore!.createCreditApplication(request);
            this.clearForm();
            successMessage(`A new credit application has been created.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editCreditApplication = async () => {
        try {
            const request = this.generateRequest();
            if (this.state.editableApplicationId === null) {
                throw new Error("In editCreditApplication with a null ID?");
            }
            await this.props.customerStore!.editCreditApplication(this.state.editableApplicationId, request);
            this.clearForm();
            successMessage(`Changes successfully saved.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editFields = (row: PermanentCreditApplication) => {
        this.setState({
            editableApplicationId: row.id,
            showForm: true,
            ...EditablePermanentCreditApplication.FromModel(row)
        });
    };

    getAttachment = async (file: AttachmentFile) => {
        try {
            const blob = await this.props.attachmentLogEndpoints!.getAttachment(file.id);
            fileUtil.DownloadBlob(file.name, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    submit = (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        event.stopPropagation();
        if (this.form && this.form.checkValidity()) {
            this.setState({ wasValidated: false });
            this.state.editableApplicationId === null ? this.createCreditApplication() : this.editCreditApplication();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    clearForm = () => {
        this.setState(DefaultState());
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const creditApplications = customerStore
            .customer!.creditApplications.slice()
            .sort((a: CreditApplication, b: CreditApplication) => b.dateCreated - a.dateCreated);

        if (!customerStore.customer || !creditApplications) {
            return <div />;
        }

        const columns = [
            {
                dataField: "dateCreated",
                text: "Date Created",
                formatter: (cellContent: any, row: CreditApplication) => {
                    return moment(row.dateCreated).format("YYYY-MM-DD");
                }
            },
            {
                dataField: "amountRequested",
                text: "Amount of Credit Requested",
                formatter: (cellContent: any, row: PermanentCreditApplication) => {
                    return row.amountRequested ? (row.amountRequested / 100).toLocaleString("en-CA", { style: "currency", currency: "CAD" }) : "N/A";
                }
            },
            {
                dataField: "notes",
                text: "Notes"
            },
            {
                dataField: "validFor",
                text: "Horse Purchased",
                formatter: (cellContent: any, row: CreditApplication | OnTheSpotCreditApplication | undefined) => {
                    console.log(row && (row as OnTheSpotCreditApplication).validFor);
                    if (row && (row as OnTheSpotCreditApplication).validFor) {
                        return <HorseForSaleLink horseForSale={(row as OnTheSpotCreditApplication).validFor} targetBlank={true} />;
                    } else {
                        return "-";
                    }
                }
            },
            {
                dataField: "state",
                text: "Status",
                formatter: (cellContent: CreditApplication | undefined, row: PermanentCreditApplication) => {
                    return CreditApplicationStateFriendlyName(row.state);
                }
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "260px", textAlign: "center" },
                formatter: (cellContent: any, row: PermanentCreditApplication) => {
                    if (row.type !== "PERMANENT") {
                        return "";
                    }
                    return (
                        <>
                            <div className="btn-group btn-group-toggle mr-1" data-toggle="buttons">
                                {row.state === CreditApplicationState.PENDING && (
                                    <>
                                        <button
                                            className="btn btn-outline-info btn-sm"
                                            onClick={this.creditApplicationStateChange(row.id, "APPROVED" as CreditApplicationState)}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            className="btn btn-outline-info btn-sm"
                                            onClick={this.creditApplicationStateChange(row.id, "REJECTED" as CreditApplicationState)}
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                                {row.state === CreditApplicationState.APPROVED && (
                                    <>
                                        <button
                                            className="btn btn-info btn-sm"
                                            onClick={this.creditApplicationStateChange(row.id, "REVOKED" as CreditApplicationState)}
                                        >
                                            Revoke
                                        </button>
                                    </>
                                )}
                            </div>

                            {row.state !== CreditApplicationState.REJECTED && row.state !== CreditApplicationState.REVOKED && (
                                <>
                                    <button
                                        className="btn btn-secondary btn-sm mr-1"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            this.editFields(row);
                                        }}
                                        id={`tooltipEdit${row.id}`}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                        Edit Credit Application
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.attachmentLog.files.length > 0 && (
                                <DropDownButton
                                    listItems={row.attachmentLog.files}
                                    onClick={attachment => this.getAttachment(attachment)}
                                    position="right"
                                    style="dark"
                                    width="240px"
                                />
                            )}
                        </>
                    );
                }
            }
        ];

        return (
            <div className="card shadow">
                <div className="card-header">Credit Application</div>
                <div className="card-body">
                    {!this.state.showForm ? (
                        <div className="row mb-4 mx-0">
                            <div className="col-12 mx-0 text-right">
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.setState({ showForm: true });
                                    }}
                                >
                                    Create Credit Application
                                </button>
                            </div>
                        </div>
                    ) : (
                        this.state.showForm && (
                            <div className="row mb-4  mx-0">
                                <div className="col-12  mx-0 px-0">
                                    <div className="card mx-0" style={{ backgroundColor: "#f7faff" }}>
                                        <div className="card-body pt-0">
                                            <form
                                                id="horse-form"
                                                ref={form => (this.form = form)}
                                                className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                                                onSubmit={e => this.submit(e)}
                                                noValidate={true}
                                            >
                                                <div className="form-row">
                                                    <InputGroup
                                                        label="Amount:"
                                                        className="col-md-12 col-lg-6"
                                                        type="string"
                                                        name="amount"
                                                        isRequired={true}
                                                        disabled={false}
                                                        value={(this.state.amountRequested && this.state.amountRequested.toString()) || ""}
                                                        onChange={e => {
                                                            this.setState({ amountRequested: e.target.value });
                                                        }}
                                                        icon={<FontAwesomeIcon icon={faDollarSign} />}
                                                    />
                                                </div>
                                                <div className="form-row">
                                                    <div className={`form-group col-md-12`}>
                                                        <label htmlFor="files">Attach File:</label>
                                                        <AttachmentsForm
                                                            attachments={this.state.attachmentLog}
                                                            updateAttachmentLog={(attachmentLog: Array<FileUploadRequest>) =>
                                                                this.setState({ attachmentLog })
                                                            }
                                                            multiple={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <TextareaGroup
                                                        label="Notes"
                                                        rows={3}
                                                        isRequired={false}
                                                        name="notes"
                                                        className="col-md-12"
                                                        value={this.state.notes}
                                                        onChange={e => this.setState({ notes: e.target.value })}
                                                    />
                                                </div>
                                                <div className="for-row text-right">
                                                    <div className="col-12 pr-0">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                this.clearForm();
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button type="submit" className="btn btn-primary btn-sm mr-0 ml-3">
                                                            {this.state.editableApplicationId ? "Edit" : "Create"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                    <div className="table-responsive">
                        <BootstrapTable
                            keyField="id"
                            remote={true}
                            hover={true}
                            bordered={false}
                            columns={columns}
                            bootstrap4={true}
                            data={creditApplications}
                            noDataIndication={() => <div>{customerStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>}
                        />
                        {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                Otherwise the `noDataIndication` wouldn't work */}
                        {customerStore.loading && <div />}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/CreditApplication.tsx